/**
 * Cockpit market registry.
 *
 * Maps the two live collectible card markets onto the 3D-object descriptors the
 * cockpit components expect (`obj`, `accent`, `packSub`, …). Only markets listed
 * here get the cockpit UI — everything else keeps the existing trading pages.
 *
 * Design → market correspondence comes from the handoff's `design/data.js`:
 *   slab / gold "THE TOP 10 GRADED CARDS"  → our `psa-10` RWA index
 *   pack / blue "ALL POKÉMON CARDS"        → our `poke` RWA index
 */

import { formatWithSubscriptString } from "../utils/formatter";

/** Route each cockpit market lives at. */
export const COCKPIT_ROUTES = {
  "psa-10": "/perps/markets/rwa/psa-10",
  poke: "/perps/markets/rwa/poke",
};

/**
 * Presentation descriptors keyed by the market id from `config/markets.js`.
 * Prices/changes are NOT held here — those come from the live oracle and the
 * /psa10 endpoints at render time.
 */
export const COCKPIT_MARKETS = {
  // PSA-10 commented out for now (market disabled in config/markets.js)
  /*
  "psa-10": {
    id: "psa-10",
    name: "Pokémon PSA10",
    short: "PSA10",
    obj: "slab",
    accent: "#E0A500",
    ring: "#8FE3FF",
    packSub: "THE TOP 10 GRADED CARDS",
    route: COCKPIT_ROUTES["psa-10"],
  },
  */
  poke: {
    id: "poke",
    name: "All Pokémon Cards",
    short: "POKE",
    obj: "pack",
    accent: "#3B4CCA",
    ring: "#E64672",
    packSub: "ALL POKÉMON CARDS",
    route: COCKPIT_ROUTES.poke,
  },
};

/** Display order in the switcher coverflow (PSA10 first, matching the design). */
export const COCKPIT_MARKET_IDS = [/* "psa-10", */ "poke"];

/**
 * @param {string} id - Market id from `config/markets.js`
 * @returns {boolean} True when this market should render the cockpit UI
 */
export const isCockpitMarket = (id) => Object.prototype.hasOwnProperty.call(COCKPIT_MARKETS, id);

/**
 * @param {string} id - Market id
 * @returns {object|null} Cockpit descriptor, or null for non-cockpit markets
 */
export const getCockpitMarket = (id) => COCKPIT_MARKETS[id] || null;

/**
 * Price formatter used across the cockpit (handoff: `T3D.fmtPrice`).
 *
 * Sub-$1 prices go through the shared subscript formatter rather than a flat
 * `toFixed(4)`: at four decimals every meme-coin collapsed to the same
 * "$0.0000", so BONK, SHIB and friends were indistinguishable from each other
 * and from zero. Returns a plain string (Unicode subscript digits) because
 * callers feed it to `NumberTicker`'s `text` prop.
 *
 * @param {number|string|null} p
 * @returns {string}
 */
export const fmtPrice = (p) => {
  const n = Number(p);
  if (p == null || Number.isNaN(n)) return "--";
  if (n < 1) return formatWithSubscriptString(n, { prefix: "$" });
  return "$" + n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
